import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import { getLotNumbers, getApplicableStandardsText } from '../data/materialLotData';
import DocumentPreview from './DocumentPreview';
import { FaTruck } from 'react-icons/fa';

const DeliveryNoteGenerator = ({ order, showButton = true, buttonLabel = "Bon de Livraison" }) => {
    const { products, clients, invoices } = useData();
    const [showPreview, setShowPreview] = useState(false);

    const items = [];
    let total = 0;
    const currentOrder = order || {};
    const practitionerClient = clients ? clients.find(c => c.id === (currentOrder?.clientId || currentOrder?.client_id)) : null;
    const currentId = currentOrder.id || 'preview';
    const targetId = `delivery-img-${currentId}`;
    const fileName = `BL-${currentOrder.patientName || 'livraison'}`;

    if (currentOrder.teethConfig) {
        if (Array.isArray(currentOrder.teethConfig)) {
            // New Array Format
            currentOrder.teethConfig.forEach(line => {
                const { productId, teeth, details } = line;
                const product = products.find(p => p.id === productId);

                if (product) {
                    let priceKey = 'priceClassique';
                    if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
                    if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

                    const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                    const quantity = line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                        ? Number(line.quantity) 
                        : (teeth.length || 1);
                    const isWarranty = Number(quantity) === 0 || Boolean(currentOrder.notes && currentOrder.notes.includes('[REDO'));
                    const itemPrice = isWarranty ? 0 : unitPrice * quantity;
                    const toothLabel = teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ');
                    let description = `${product.name}`;

                    if (details && details.teinte) {
                        description += `, Teinte ${details.teinte}`;
                    }

                    items.push({ 
                        toothId: toothLabel, 
                        description, 
                        quantity: isWarranty ? 0 : quantity, 
                        unitPrice, 
                        price: itemPrice, 
                        notes: line.notes, 
                        category: product.category, 
                        details: details || {} 
                    });
                    total += itemPrice;
                }
            });
        } else {
            // Legacy Object Format
            Object.entries(currentOrder.teethConfig).forEach(([toothId, config]) => {
                const { productId, details } = config;
                const product = products.find(p => p.id === productId);

                if (product) {
                    let priceKey = 'priceClassique';
                    if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
                    if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

                    const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                    const isWarranty = Boolean(currentOrder.notes && currentOrder.notes.includes('[REDO'));
                    const itemPrice = isWarranty ? 0 : unitPrice;
                    let description = `${product.name}`;

                    if (details && details.teinte) {
                        description += `, Teinte ${details.teinte}`;
                    }

                    items.push({ 
                        toothId, 
                        description, 
                        quantity: isWarranty ? 0 : 1, 
                        unitPrice, 
                        price: itemPrice, 
                        notes: config.notes, 
                        category: product.category, 
                        details: details || {} 
                    });
                    total += itemPrice;
                }
            });
        }
    }

    // Fallback si teethConfig est vide mais qu'une facture existe déjà
    if (items.length === 0 && currentOrder.id) {
        const existingInv = (invoices || []).find(inv => 
            (inv.orderId && inv.orderId === currentOrder.id) || 
            (inv.order_id && inv.order_id === currentOrder.id) || 
            (currentOrder.invoiceId && inv.id === currentOrder.invoiceId)
        );
        if (existingInv && Array.isArray(existingInv.items) && existingInv.items.length > 0) {
            existingInv.items.forEach(it => {
                const qty = it.quantity || 1;
                const unitPrice = it.unitPrice || (it.price ? it.price / qty : 0);
                items.push({
                    toothId: it.toothId || '—',
                    description: it.description || it.name || 'Prothèse',
                    quantity: qty,
                    unitPrice,
                    price: it.price || 0,
                    category: it.category || 'PROTHÈSE'
                });
            });
            total = existingInv.amount || items.reduce((sum, it) => sum + (it.price || 0), 0);
        }
    }

    // Calcul précis des lots et des normes applicables
    const detectedLots = [];
    const seenLotLabels = new Set();
    items.forEach(item => {
        const lots = getLotNumbers(item.category, item.description, item.details?.teinte, item.notes);
        lots.forEach(l => {
            if (!seenLotLabels.has(l.label)) {
                seenLotLabels.add(l.label);
                detectedLots.push(l);
            }
        });
    });

    return (
        <>
            {showButton && (
                <button
                    className="btn btn-sm btn-outline-dark"
                    onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
                >
                    <FaTruck className="me-1" />
                    {buttonLabel}
                </button>
            )}

            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Hidden Template for Image Capture */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px', borderBottom: '3px solid #1e293b', paddingBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.address}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>Tel: {laboratoryInfo.phone}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.email}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ margin: '0 0 10px 0', color: '#1e293b', fontSize: '28px' }}>BON DE LIVRAISON</h1>
                            <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '14px' }}>
                                N° DOSSIER: {currentOrder.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW')}
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '14px' }}>Date de livraison: {new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    {/* Info Section */}
                    <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: '50%', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                            <h3 style={{ marginTop: '0', fontSize: '14px', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Livré à:</h3>
                            <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '16px', color: '#1e293b' }}>Docteur {currentOrder.practitionerName}</p>
                            {practitionerClient?.address && <p style={{ margin: '5px 0', fontSize: '13px', color: '#475569' }}>{practitionerClient.address}</p>}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>Patient: {currentOrder.patientName}</p>
                            {(currentOrder.patientReference || currentOrder.patient_reference) && (
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>Référence : <strong>{currentOrder.patientReference || currentOrder.patient_reference}</strong></p>
                            )}
                            {currentOrder.gamme && (
                                <p style={{ margin: '5px 0', fontSize: '13.5px' }}><strong>Gamme :</strong> {currentOrder.gamme.toLowerCase() === 'nova' ? 'Finition Nova' : currentOrder.gamme.toUpperCase()}</p>
                            )}
                        </div>
                    </div>

                    {Boolean((currentOrder.notes && currentOrder.notes.includes('[REDO')) || items.some(it => Number(it.quantity) === 0)) && (
                        <div style={{ backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: '4px', padding: '8px 12px', margin: '15px 0', color: '#92400e', fontWeight: 'bold', fontSize: '13px' }}>
                            🔄 LIVRAISON TRAVAIL À REFAIRE (SOUS GARANTIE)
                        </div>
                    )}

                    {/* Table of Items */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: '13px' }}>Désignation des travaux</th>
                                <th style={{ textAlign: 'center', padding: '12px 4px', width: '110px', fontSize: '13px' }}>Dent</th>
                                <th style={{ textAlign: 'center', padding: '12px', width: '65px', fontSize: '13px' }}>Qté</th>
                                <th style={{ textAlign: 'right', padding: '12px', width: '90px', fontSize: '13px' }}>P.U. HT</th>
                                <th style={{ textAlign: 'right', padding: '12px 14px', width: '100px', fontSize: '13px' }}>Montant HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => {
                                const isZeroQty = Number(item.quantity) === 0;
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '12px 14px', fontSize: '13.5px' }}>{item.description}</td>
                                        <td style={{ textAlign: 'center', padding: '12px 4px', fontSize: '13px', fontWeight: 'bold', lineHeight: '1.4' }}>{item.toothId}</td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '13.5px' }}>
                                            {isZeroQty ? (
                                                <span style={{ display: 'inline-block', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                    0 (Garantie)
                                                </span>
                                            ) : (
                                                item.quantity
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '12px', fontSize: '13.5px', color: '#334155' }}>
                                            {item.unitPrice !== undefined ? `${Number(item.unitPrice).toFixed(2)} €` : '—'}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '12px 14px', fontSize: '13.5px', fontWeight: 'bold', color: '#1e293b' }}>
                                            {isZeroQty ? '0.00 €' : `${Number(item.price).toFixed(2)} €`}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Bloc Récapitulatif Total Prix */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '25px' }}>
                        <div style={{ width: '280px', backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13.5px' }}>
                                <span style={{ color: '#64748b' }}>Total HT :</span>
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{total.toFixed(2)} €</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0 0', borderTop: '2px solid #1e293b', marginTop: '6px', fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }}>
                                <span>TOTAL LIVRAISON :</span>
                                <span>{total.toFixed(2)} €</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes & Remarques Section */}
                    {currentOrder.notes && currentOrder.notes.trim() !== '' && (
                        <div style={{ marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1e293b', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>NOTES & REMARQUES</h4>
                            <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                <strong>Remarque générale :</strong> {currentOrder.notes}
                            </div>
                        </div>
                    )}

                    {/* Traçabilité Section */}
                    <div style={{ marginTop: '15px', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px dashed #cbd5e1' }}>
                        <span style={{ fontSize: '11px', color: '#475569' }}><strong>Traçabilité des composants & Normes ISO / UE :</strong></span>
                        <div style={{ fontSize: '10.5px', color: '#334155', marginTop: '4px', lineHeight: '1.7' }}>
                            {detectedLots.length > 0 ? (
                                detectedLots.map((lot, idx) => (
                                    <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                ))
                            ) : (
                                <div>Matériaux : Lot ____________________</div>
                            )}
                        </div>
                    </div>

                    {/* Déclaration d'engagement et de conformité DM sur mesure */}
                    <div style={{ marginTop: '15px', border: '1px solid #cbd5e1', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px', textTransform: 'uppercase' }}>
                            Déclaration de Conformité — Dispositif Médical sur Mesure (DMSM)
                        </div>
                        <p style={{ margin: '0', fontSize: '10px', color: '#475569', lineHeight: '1.4', textAlign: 'justify' }}>
                            Le laboratoire <strong>{laboratoryInfo.name}</strong> certifie que ce dispositif médical sur mesure a été fabriqué exclusivement pour le patient susnommé conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances du <strong>Règlement Européen (UE) 2017/745 (MDR)</strong> et {getApplicableStandardsText(detectedLots)}
                        </p>
                    </div>

                    {/* Signature Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginTop: '25px', paddingTop: '15px', borderTop: '2px solid #e2e8f0', fontSize: '12px' }}>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '95px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e293b' }}>Visa Laboratoire</span>
                            <div style={{ textAlign: 'center', margin: '4px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src="/signature.png" alt="Signature" style={{ height: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                            </div>
                        </div>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '95px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e293b' }}>Signature de Réception</span>
                            <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: 'auto', paddingTop: '6px', borderTop: '1px dashed #cbd5e1' }}>
                                Nom, Date & Signature du praticien
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{
                        marginTop: '20px',
                        textAlign: 'center',
                        fontSize: '9.5px',
                        color: '#94a3b8',
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: '8px'
                    }}>
                        <p style={{ fontWeight: 'bold', margin: '0 0 3px 0' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                        <p style={{ margin: '2px 0', fontStyle: 'italic' }}>Système de management de la qualité conforme aux exigences de la norme ISO 13485 (Dispositifs Médicaux)</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DeliveryNoteGenerator;
